/* eslint-disable @typescript-eslint/ban-types */
import {PureComponent} from 'react';
import {Overlay} from 'react-bootstrap';
import memoize from 'memoize-one';

import {popOverOverlayPosition} from 'utils/position_utils';
import {Constants} from 'utils/constants';

import EmojiPickerTabs from './emoji_picker_tabs';

type Props = {
    show: boolean;
    target: any;
    onEmojiClick: Function;
    onHide: Function;
    topOffset?: number;
    rightOffset?: number;
    leftOffset?: number;
    spaceRequiredAbove?: number;
    spaceRequiredBelow?: number;
    defaultHorizontalPosition?: 'left' | 'right';
};
export default class EmojiPickerOverlay extends PureComponent<Props> {
    // текущая высота пикера - 442px
    // учет topOffset перенесен в popOverOverlayPosition
    // An emoji picker in the center channel is contained within the post list, so it needs space
    // above for the channel header and below for the post textbox
    // высота шапки - 40px
    static CENTER_SPACE_REQUIRED_ABOVE = 482;

    // текущая высота + 10px
    static CENTER_SPACE_REQUIRED_BELOW = 452;

    // An emoji picker in the RHS isn't constrained by the RHS, so it just needs space to fit
    // the emoji picker itself
    static RHS_SPACE_REQUIRED_ABOVE = 442;

    static RHS_SPACE_REQUIRED_BELOW = 452;

    // Reasonable defaults calculated from from the center channel
    static defaultProps = {
        spaceRequiredAbove: EmojiPickerOverlay.CENTER_SPACE_REQUIRED_ABOVE,
        spaceRequiredBelow: EmojiPickerOverlay.CENTER_SPACE_REQUIRED_BELOW,
    };

    private handleClickOutside = (e: MouseEvent) => {
        if (!this.props.show) {
            return;
        }

        // @TODO: ПЕРЕЙТИ НА НОРМАЛЬНУЮ ЛИБУ ДЛЯ ОВЕРЛЕЕВ/ПОПАПОВ
        const tabs = document.getElementById('emoji-picker-tabs');
        const picker = document.getElementById('emojiPicker');

        if (tabs?.contains(e.target) || picker?.contains(e.target)) {
            return;
        }

        this.props.onHide();
    };

    private handleKeyUp = (e: KeyboardEvent) => {
        if (e.key === Constants.KeyCodes.ESCAPE[0]) {
            this.props.onHide();
        }
    }

    componentDidMount(): void {
        if (this.props.show) {
            this.addEventListeners();
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (this.props.show && !prevProps.show) {
            setTimeout(() => {
                this.addEventListeners();
            }, 300);
        }

        if (!this.props.show && prevProps.show) {
            this.removeEventListeners();
        }
    }

    private addEventListeners = () => {
        window.addEventListener('click', this.handleClickOutside);
        window.addEventListener('keyup', this.handleKeyUp);
    }

    private removeEventListeners = () => {
        window.removeEventListener('click', this.handleClickOutside);
        window.removeEventListener('keyup', this.handleKeyUp);
    }

    componentWillUnmount(): void {
        this.removeEventListeners();
    }

    emojiPickerPosition = memoize((emojiTrigger, show) => {
        let calculatedRightOffset: number = Constants.DEFAULT_EMOJI_PICKER_RIGHT_OFFSET;

        if (!show) {
            return calculatedRightOffset;
        }

        if (emojiTrigger) {
            calculatedRightOffset =
                window.innerWidth -
                emojiTrigger.getBoundingClientRect().left -
                Constants.DEFAULT_EMOJI_PICKER_LEFT_OFFSET;

            if (calculatedRightOffset < Constants.DEFAULT_EMOJI_PICKER_RIGHT_OFFSET) {
                calculatedRightOffset = Constants.DEFAULT_EMOJI_PICKER_RIGHT_OFFSET;
            }
        }

        return calculatedRightOffset;
    });

    getPlacement = memoize((
        target,
        spaceRequiredAbove,
        spaceRequiredBelow,
        topOffset,
        defaultHorizontalPosition,
        show,
    ) => {
        if (!show) {
            return 'top';
        }

        if (target) {
            const targetBounds = target.getBoundingClientRect();
            return popOverOverlayPosition(
                targetBounds,
                window.innerHeight,
                spaceRequiredAbove,
                spaceRequiredBelow,
                topOffset,
                defaultHorizontalPosition,
            );
        }

        return 'top';
    });

    render() {
        const {
            target,
            rightOffset,
            spaceRequiredAbove,
            spaceRequiredBelow,
            defaultHorizontalPosition,
            show,
            topOffset,
        } =
            this.props;

        const calculatedRightOffset =
            typeof rightOffset === 'undefined' ? this.emojiPickerPosition(target(), show) : rightOffset;
        const placement = this.getPlacement(
            target(),
            spaceRequiredAbove,
            spaceRequiredBelow,
            topOffset,
            defaultHorizontalPosition,
            show,
        );

        return (
            <Overlay
                show={show}
                placement={placement}
                rootClose={false}
                onHide={this.props.onHide}
                target={target}
                animation={false}
            >
                <EmojiPickerTabs
                    onEmojiClose={this.props.onHide}
                    onEmojiClick={this.props.onEmojiClick}
                    rightOffset={calculatedRightOffset}
                    topOffset={this.props.topOffset}
                    leftOffset={this.props.leftOffset}
                />
            </Overlay>
        );
    }
}
